import IconButton from '@components/Common/IconButton.js';
import Popover from '@components/Common/Popover.js';
import Search from '@components/Common/Search.js';
import toast from '@components/Common/Toast.js';
import YesNoPromptModal from '@components/Modals/YesNoPromptModal.js';
import { IS_MOBILE } from '@constants/global';
import { UIIcon } from '@icons/index.js';
import { COLUMN_EVENT_TYPES, columnUpdate } from '@signals/project/index.js';
import ProjectsState from '@states/Projects';
import { UICheckbox, UIDiv, UIText } from '@ui';
import {
  defaultColumns,
  getFolderColumns,
  resetCustomOrder,
  setColumnVisibility,
} from '@utils/columnUtils';

export default class ProjectColumnFilter extends UIDiv {
  constructor(currentFolderId, accessControl = null) {
    super();

    this.currentFolderId = currentFolderId;
    this.canUpdate = accessControl && accessControl.hasPermission('update');
    this.columns = getFolderColumns(currentFolderId);
    this.searchQuery = '';
    this.filteredColumns = [...this.columns];

    // Subscribe to all column events
    columnUpdate.add(data => {
      this.handleColumnChange(data);
    });

    // We need to update filter at the project fetch
    ProjectsState.subscribe(() => {
      this.updateCurrentFolderId(currentFolderId, true);
    });

    this.render();
  }

  /**
   * Get the appropriate icon for a column type
   * @param {string} columnType - The column type (select, text, people, date, number)
   * @returns {UIIcon} Icon instance for the column type
   */
  getColumnIcon(columnType) {
    const iconConfig = defaultColumns.find(col => col.key === columnType);
    // Fallback to UIIcon with icon name
    return new UIIcon(iconConfig.icon, {
      size: 16,
      color: 'none',
      strokeColor: 'white',
      className: `${iconConfig.bgColor} !w-6 !h-6 rounded-md flex items-center justify-center p-0.5`,
    });
  }

  render() {
    this.columnFilter = new IconButton({
      iconName: 'columns',
      color: 'none',
      text: IS_MOBILE() ? '' : i18next.t('pages.projects.columns.title'),
      iconSize: IS_MOBILE() ? 21 : 16,
      onClick: () => {}, // Will be set after popover creation
      buttonClass: 'min-h-8 border border-gray-300 bg-white hover:bg-gray-50',
    });

    // Create popover content
    const popoverContent = this.createPopoverContent();

    // Create popover
    this.columnFilter.onClick(e => {
      e.stopPropagation();
      Popover.show({
        trigger: this.columnFilter,
        content: popoverContent,
        placement: 'bottom-start',
        hideOnSelect: false,
        hideOnScroll: false,

        className: 'w-80 flex flex-col',
      });
    });

    this.add(this.columnFilter);
  }

  createPopoverContent() {
    const container = new UIDiv().setClass('flex flex-col h-full');

    // Header with counter and action buttons
    const header = this.createHeader();
    container.add(header);

    // Reset button
    const resetButton = this.createResetButton();
    container.add(resetButton);

    // Search bar
    const searchContainer = this.createSearchBar();
    container.add(searchContainer);

    // Columns list
    this.columnsList = this.createColumnsList();
    container.add(this.columnsList);

    return container;
  }

  createHeader() {
    const header = new UIDiv().setClass(
      'flex items-center gap-3 p-4 border-b border-gray-200',
    );

    // Master checkbox for "All columns"
    const allColumnsContainer = new UIDiv().setClass(
      'flex items-center gap-3 cursor-pointer',
    );

    // Master checkbox
    this.masterCheckboxContainer = new UIDiv();
    this.updateMasterCheckbox();

    // "All columns" label with counter
    this.allColumnsContainer = new UIDiv().setClass('flex items-center gap-1');
    this.allColumnsLabel = new UIText(
      i18next.t('pages.projects.columns.allColumns'),
    );
    this.allColumnsLabel.setClass('text-gray-900 font-semibold');
    this.allColumnsCount = new UIText();
    this.allColumnsCount.setClass('text-gray-900');
    this.updateAllColumnsText();

    this.allColumnsContainer.add(this.allColumnsLabel);
    this.allColumnsContainer.add(this.allColumnsCount);

    // Click handler for the entire container
    allColumnsContainer.onClick(e => {
      e.stopPropagation();
      this.toggleAllColumns();
    });

    allColumnsContainer.add(this.masterCheckboxContainer);
    allColumnsContainer.add(this.allColumnsContainer);

    header.add(allColumnsContainer);

    return header;
  }

  createResetButton() {
    const resetContainer = new UIDiv().setClass(
      'relative p-4 border-b border-gray-200',
    );
    const resetButton = new IconButton({
      iconName: 'reset',
      color: 'none',
      text: i18next.t('pages.projects.columns.reset'),
      iconSize: 16,
      onClick: async () => {
        const modal = new YesNoPromptModal(
          i18next.t('pages.projects.columns.reset'),
          i18next.t('pages.projects.reset_column_order_confirm'),
          {
            confirmText: i18next.t('common.reset'),
            cancelText: i18next.t('common.cancel'),
          },
        );
        Popover.hide(); // Hide the popover after confirmation
        const result = await modal.show();
        if (result) {
          try {
            await resetCustomOrder(this.currentFolderId);
          } catch (error) {
            console.error('Failed to reset columns:', error);
            toast.error(`Failed to reset columns: ${error.message}`);
          }
        }
      },
      buttonClass: 'bg-white hover:bg-gray-50 w-full',
    });
    resetContainer.add(resetButton);
    return resetContainer;
  }

  createSearchBar() {
    const search = new Search({
      handleSearch: searchValue => {
        this.handleSearch(searchValue);
      },
      className: 'm-4',
      inputId: 'column-search-input',
      placeholder: i18next.t('pages.projects.columns.search'),
    });

    return search;
  }

  createColumnsList() {
    const listContainer = new UIDiv().setClass(
      'flex-1 overflow-y-auto max-h-64 border-t border-gray-200',
    );
    this.columnsListContainer = new UIDiv().setClass('p-2');

    this.updateColumnsList(this.columnsListContainer);
    listContainer.add(this.columnsListContainer);

    return listContainer;
  }

  updateColumnsList(container) {
    // Clear all children from the container
    container.clear();

    this.filteredColumns.forEach(column => {
      const columnItem = this.createColumnItem(column);
      container.add(columnItem);
    });

    // Show "No columns found" if search has no results
    if (this.filteredColumns.length === 0 && this.searchQuery) {
      const noResults = new UIDiv().setClass('text-center py-8 text-gray-500');
      noResults.add(new UIText('No columns found'));
      container.add(noResults);
    }
  }

  createColumnItem(column) {
    const item = new UIDiv().setClass(
      'flex items-center gap-3 p-1.5 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors',
    );

    // Use UICheckbox for individual columns
    const checkbox = new UICheckbox('', column.isVisible, false);
    checkbox.input.classname =
      'w-6 h-6 text-[var(--o-hive-primary-80)] border-gray-300';

    // Get column icon based on type
    const columnIcon = this.getColumnIcon(column.type);

    // Column name
    const columnName = new UIText(column.title);
    columnName.setClass('flex-1');

    // Fixed indicator (for fixed columns like Name)
    // Determine the right status text for the indicator
    const statusKey = column.isFixed
      ? 'fixed'
      : column.isCustom
        ? 'custom'
        : 'default';
    const indicator = new UIText(
      `(${i18next.t(`pages.projects.columns.${statusKey}`)})`,
    );
    indicator.setClass('text-xs text-gray-400 ml-2');

    // Create the container for the name and indicator
    const nameContainer = new UIDiv().setClass(
      'flex items-center flex-1 truncate',
    );
    nameContainer.add(columnName, indicator);

    // Add all the common elements to the item once
    item.add(checkbox, columnIcon, nameContainer);

    // Now, handle the differences based on whether the column is fixed
    if (column.isFixed) {
      // Style and disable fixed columns
      columnName.addClass('text-gray-500');
      item.addClass('opacity-60 cursor-not-allowed');
      checkbox.input.disabled = true;
    } else {
      // Add click handlers for non-fixed columns
      const toggleColumnHandler = e => {
        e.stopPropagation();
        this.toggleColumn(column);
      };

      item.onClick(toggleColumnHandler);
      checkbox.input.addEventListener('change', toggleColumnHandler);
    }

    return item;
  }

  handleSearch(query) {
    this.searchQuery = query.toLowerCase();
    this.filteredColumns = this.columns.filter(column =>
      column.title.toLowerCase().includes(this.searchQuery),
    );

    // Update the columns list
    if (this.columnsListContainer) {
      this.updateColumnsList(this.columnsListContainer);
    }
  }

  async toggleColumn(column) {
    if (column.isFixed) return;

    try {
      if (column.id) {
        // Set a flag to indicate this component is making the change
        this.isUpdatingColumn = true;

        await setColumnVisibility(
          this.currentFolderId,
          column.id,
          !column.isVisible,
          this.canUpdate,
        );

        // Clear the flag after a short delay to allow the event to propagate
        setTimeout(() => {
          this.isUpdatingColumn = false;
        }, 100);

        // Update the display immediately for better UX
        this.updateDisplay();
      }
    } catch (error) {
      this.isUpdatingColumn = false;
      console.error('Failed to hide column:', error);
      toast.error(
        `Failed to ${column.isVisible ? 'hide' : 'show'} column: ${error.message}`,
      );
    }
  }

  updateDisplay() {
    // Refresh columns data from the folder state
    this.columns = getFolderColumns(this.currentFolderId);
    this.filteredColumns = this.columns.filter(column =>
      column.title?.toLowerCase().includes(this.searchQuery),
    );

    // Update master checkbox and text
    this.updateMasterCheckbox();
    this.updateAllColumnsText();

    // Update columns list and individual checkboxes
    if (this.columnsListContainer) {
      this.updateColumnsList(this.columnsListContainer);
    }
  }

  /**
   * Handle column events from external sources
   * @param {Object} data - The column event data
   */
  handleColumnChange(data) {
    // Only respond to changes from the same folder and not from this component itself
    if (
      data.currentFolderId === this.currentFolderId &&
      !this.isUpdatingColumn
    ) {
      switch (data.type) {
        case COLUMN_EVENT_TYPES.VISIBILITY_CHANGED:
        case COLUMN_EVENT_TYPES.TITLE_CHANGED:
        case COLUMN_EVENT_TYPES.DELETED:
        case COLUMN_EVENT_TYPES.ADDED:
        case COLUMN_EVENT_TYPES.RESET:
        case COLUMN_EVENT_TYPES.ORDERING:
          this.updateDisplay();
          break;

        default:
          break;
      }
    }
  }

  /**
   * Update the current folder ID and refresh the column filter content
   * Call this when navigating between folders
   * @param {string|null} newFolderId - The new folder ID (null for root projects)
   */
  updateCurrentFolderId(newFolderId, isProjectTypeChange = false) {
    // Only update if the folder ID actually changed
    if (this.currentFolderId !== newFolderId || isProjectTypeChange) {
      this.currentFolderId = newFolderId;

      // Refresh columns data and display
      this.updateDisplay();
    }
  }

  /**
   * Cleanup method to remove event subscriptions
   * Call this when the component is destroyed
   */
  destroy() {
    if (this.unsubscribeFromColumnChanges) {
      this.unsubscribeFromColumnChanges();
    }
  }

  updateMasterCheckbox() {
    const nonFixedColumns = this.columns.filter(col => !col.isFixed);
    const visibleNonFixedColumns = nonFixedColumns.filter(col => col.isVisible);

    // Remove existing checkbox if any
    this.masterCheckboxContainer.clear();

    // Create new UICheckbox
    const isAllSelected =
      visibleNonFixedColumns.length === nonFixedColumns.length;
    const isIndeterminate = visibleNonFixedColumns.length > 0 && !isAllSelected;

    this.masterCheckbox = new UICheckbox('', isAllSelected, false);

    // Set indeterminate state if needed
    if (isIndeterminate) {
      this.masterCheckbox.input.indeterminate = true;
    }

    // Add click handler to the checkbox input
    this.masterCheckbox.input.addEventListener('change', e => {
      e.stopPropagation();
      this.toggleAllColumns();
    });

    this.masterCheckboxContainer.add(this.masterCheckbox);
  }

  updateAllColumnsText() {
    const nonFixedColumns = this.columns.filter(col => !col.isFixed);
    const visibleCount = nonFixedColumns.filter(col => col.isVisible).length;
    this.allColumnsCount.setValue(`(${visibleCount} selected)`);
  }

  async toggleAllColumns() {
    try {
      // Set a flag to indicate this component is making the changes
      this.isUpdatingColumn = true;

      const nonFixedColumns = this.columns.filter(col => !col.isFixed);
      const visibleNonFixedColumns = nonFixedColumns.filter(
        col => col.isVisible,
      );

      // If all non-fixed columns are visible, hide them all
      // Otherwise, show them all
      const shouldHideAll =
        visibleNonFixedColumns.length === nonFixedColumns.length;

      const promiseShowALl = this.columns.map(column => {
        if (column.isFixed) return; // Skip fixed columns
        return setColumnVisibility(
          this.currentFolderId,
          column.id,
          !shouldHideAll,
          this.canUpdate,
        );
      });

      // Wait for all promises to resolve
      await Promise.all(promiseShowALl);

      // Clear the flag after a short delay
      setTimeout(() => {
        this.isUpdatingColumn = false;
      }, 100);

      // Update the display after toggling
      this.updateDisplay();
    } catch (error) {
      this.isUpdatingColumn = false;
      console.error('Error toggling all columns:', error);
      toast.error(`Error toggling all columns: ${error.message}`);
    }
  }
}
